import { ref, computed } from "vue";
import { Flash } from "~/jose_modern/flash";

const CLEAR_CONFIGS = {
  dvLines: {
    title: "Clear All Ingredients",
    getMessage: (store) => `Remove all ${store.dvLines.length} ingredient(s)?`,
    confirmText: "Remove All",
    successMessage: "Cleared all ingredients with daily values",
  },
  lines: {
    title: "Clear All Ingredients",
    getMessage: (store) => `Remove all ${store.linesWithoutDv.length} ingredient(s)?`,
    confirmText: "Remove All",
    successMessage: "Cleared all ingredients without daily values",
  },
  blends: {
    title: "Clear All Blends",
    getMessage: (store) => `Remove all ${store.blends.length} blend(s)?`,
    confirmText: "Remove All",
    successMessage: "All blends cleared",
  },
  dietary: {
    title: "Clear Dietary Information",
    getMessage: () => "Are you sure you want to clear all dietary values?",
    confirmText: "Remove All",
    successMessage: "Cleared dietary information",
  },
};

export function useSupplementClearOperations(store, save) {
  const pendingClear = ref(null);
  const isClearing = ref(false);

  const confirmConfig = computed(() => {
    if (!pendingClear.value) return null;
    const config = CLEAR_CONFIGS[pendingClear.value];
    if (!config) return null;
    return {
      title: config.title,
      message: config.getMessage(store),
      confirmText: config.confirmText,
    };
  });

  const showClearConfirm = computed({
    get: () => !!pendingClear.value,
    set: (val) => {
      if (!val) pendingClear.value = null;
    },
  });

  function requestClear(type) {
    if (!CLEAR_CONFIGS[type]) {
      console.warn(`Unknown clear type: ${type}`);
      return;
    }
    const counts = {
      dvLines: store.dvLines.length,
      lines: store.linesWithoutDv.length,
      blends: store.blends.length,
      dietary: store.dietaryLines.filter((l) => l.amount).length,
    };
    if (counts[type] === 0) return;

    pendingClear.value = type;
  }

  function cancelClear() {
    pendingClear.value = null;
  }

  async function performClear() {
    if (!pendingClear.value) return;

    const type = pendingClear.value;
    const config = CLEAR_CONFIGS[type];

    isClearing.value = true;
    try {
      switch (type) {
        case "dvLines":
          store.clearAllDvLines();
          save();
          break;
        case "lines":
          store.clearAllLines();
          save();
          break;
        case "dietary":
          store.clearAllDietaryLines();
          save();
          break;
        case "blends":
          store.clearAllBlends();
          save();
          break;
      }
      Flash.success(config.successMessage);
    } catch (e) {
      Flash.error(e.message);
    } finally {
      isClearing.value = false;
      pendingClear.value = null;
    }
  }

  return {
    pendingClear,
    showClearConfirm,
    confirmConfig,
    isClearing,
    requestClear,
    cancelClear,
    performClear,
  };
}
